(() => {
  function parse(card) {
    const data = JSON.parse(card.querySelector('[data-card-data]').textContent);
    return {el: card, data: {...data.record, group: data.group, colors: data.colors}};
  }
  function group(entries) {
    const groups = new Map();
    for (const entry of entries) {
      const key = entry.data.group || String(entry.data.id);
      let item = groups.get(key);
      if (!item) {
        item = {key, el: entry.el, records: new Map(), selected: entry.data.id};
        groups.set(key, item);
      }
      for (const record of [entry.data, ...(entry.data.colors || [])]) item.records.set(String(record.id), record);
    }
    return [...groups.values()];
  }
  function matches(record, filter = {}) {
    return (!filter.color || record.color === filter.color) && (!filter.fit || record.fit === filter.fit) && record.variants.some(v =>
      (!filter.size || v.options.includes(filter.size)) && (!filter.stock || v.available) && v.price >= (filter.min ?? 0) && v.price <= (filter.max ?? Infinity)
    );
  }
  function gallery(record, card) {
    const node = document.createElement('enriq-card-gallery');
    node.className = 'eq-card__media';
    node.setAttribute('aria-label', record.title);
    const track = document.createElement('a');
    track.className = 'eq-card__track'; track.href = record.url; track.draggable = false;
    track.setAttribute('aria-label', record.title);
    for (const photo of record.images) {
      const slide = document.createElement('span'); slide.className = 'eq-card__slide';
      const img = document.createElement('img'); img.className = 'eq-card__image'; img.src = photo.url;
      img.srcset = `${photo.small} 450w, ${photo.url} 900w`; img.sizes = '(min-width: 990px) 25vw, 50vw';
      img.alt = photo.alt; img.width = photo.width; img.height = photo.height; img.loading = 'lazy'; img.draggable = false;
      slide.append(img); track.append(slide);
    }
    node.append(track);
    if (record.images.length > 1) {
      for (const [step, label, text] of [[-1, card.dataset.previousLabel, '‹'], [1, card.dataset.nextLabel, '›']]) {
        const button = document.createElement('button'); button.type = 'button'; button.className = `eq-card__arrow eq-card__arrow--${step < 0 ? 'prev' : 'next'}`;
        button.dataset.cardStep = step; button.setAttribute('aria-label', label); button.textContent = text; node.append(button);
      }
      const dots = document.createElement('div'); dots.className = 'eq-card__dots'; dots.setAttribute('aria-label', card.dataset.galleryLabel.replace(/&#39;|&#x27;/g, "'"));
      record.images.forEach((photo, index) => {
        const button = document.createElement('button'); button.type = 'button'; button.dataset.cardPhoto = index;
        button.setAttribute('aria-label', card.dataset.photoLabel.replace('__number__', index + 1)); button.setAttribute('aria-current', String(index === 0)); button.append(document.createElement('span')); dots.append(button);
      }); node.append(dots);
    }
    if (!record.available) { const badge = document.createElement('span'); badge.className = 'eq-card__badge'; badge.textContent = card.dataset.soldLabel; node.append(badge); }
    return node;
  }
  function select(item, id, focus = false) {
    const record = item.records.get(String(id));
    if (!record) return;
    const card = item.el;
    if (String(item.selected) !== String(id) || !card.dataset.groupReady) {
      card.querySelector('enriq-card-gallery').replaceWith(gallery(record, card));
      card.querySelector('.eq-card__meta > span').textContent = record.color || '';
      const title = card.querySelector('.eq-card__line h3 a'); title.textContent = record.name; title.href = record.url;
      const price = card.querySelector('.eq-card__price'); price.replaceChildren();
      if (record.compare) { const old = document.createElement('s'); old.textContent = record.compare; price.append(old); }
      const current = document.createElement('span'); current.textContent = record.money; price.append(current);
    }
    item.selected = record.id; card.dataset.groupReady = 'true'; card.dataset.selectedProduct = record.id;
    card.querySelectorAll('[data-color-product-id]').forEach(swatch => {
      if (swatch.dataset.colorProductId === String(record.id)) swatch.setAttribute('aria-current', 'true'); else swatch.removeAttribute('aria-current');
    });
    card.querySelector('.eq-card__meta > span').setAttribute('aria-live', 'polite');
    if (focus) card.querySelector(`[data-color-product-id="${record.id}"]`)?.focus({preventScroll: true});
  }
  function bind(grid, groups, signal) {
    grid.addEventListener('click', event => {
      const link = event.target.closest('a[data-color-product-id]');
      if (!link || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const item = groups.find(g => g.el === link.closest('[data-eq-card]'));
      if (!item?.records.has(link.dataset.colorProductId)) return;
      event.preventDefault(); event.stopPropagation(); select(item, link.dataset.colorProductId, true);
    }, {signal});
  }
  async function initialize(grid) {
    if (grid.dataset.groupedReady) return;
    grid.dataset.groupedReady = 'true';
    const controller = new AbortController();
    try {
      const entries = [...grid.querySelectorAll('[data-eq-card]')].map(parse);
      const extras = [...grid.children].filter(el => !el.matches('[data-eq-card]'));
      let next = grid.closest('[data-grouped-search]')?.querySelector('[data-grouped-next]')?.href;
      if (grid.closest('[data-grouped-search]') && new URL(location.href).searchParams.has('page')) {
        const first = new URL(location.href); first.searchParams.delete('page');
        entries.length = 0; extras.length = 0; next = first.href;
      }
      const visited = new Set();
      while (next) {
        const url = new URL(next, location.href);
        if (url.origin !== location.origin || url.pathname !== location.pathname || visited.has(url.href)) throw Error('Invalid search page');
        visited.add(url.href);
        const response = await fetch(url, {signal: controller.signal, credentials: 'same-origin'});
        if (!response.ok) throw Error('Search unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const other = doc.querySelector('[data-grouped-search]');
        if (!other) throw Error('Search section missing');
        entries.push(...[...other.querySelectorAll('[data-eq-card]')].map(parse));
        extras.push(...other.querySelectorAll('.enriq-search-page__result'));
        next = other.querySelector('[data-grouped-next]')?.getAttribute('href');
      }
      const groups = group(entries); grid.replaceChildren(...groups.map(g => g.el), ...extras);
      groups.forEach(g => select(g, g.selected)); bind(grid, groups, controller.signal);
      grid.closest('[data-grouped-search]')?.querySelector('[data-grouped-pagination]')?.setAttribute('hidden', '');
    } catch (error) { grid.removeAttribute('data-grouped-ready'); }
  }
  window.ENRIQCards = {parse, group, matches, select, bind};
  function start(root = document) { root.querySelectorAll('[data-grouped-grid]').forEach(initialize); }
  start(); document.addEventListener('shopify:section:load', event => start(event.target));
})();
